import type { Campaign, CampaignProgress, CampaignRequirement } from './types';

// ── Polish September Campaign ────────────────────────────────────────

export const POLISH_SEPTEMBER_CAMPAIGN: Campaign = {
  id: 'polish-september',
  name: 'Kampania Wrześniowa',
  description: 'Polska kampania obronna we wrześniu 1939.',
  faction: 'poland',
  nodes: [
    {
      scenarioId: 'westerplatte',
      name: 'Westerplatte',
      description: 'Obrona Wojskowej Składnicy Tranzytowej na Westerplatte.',
    },
    {
      scenarioId: 'krojanty',
      name: 'Szarża pod Krojanty',
      description: 'Szarża 18. Pułku Ułanów Pomorskich pod Krojanty.',
      requires: [{ type: 'completed', scenarioId: 'westerplatte' }],
    },
    {
      scenarioId: 'bzura',
      name: 'Bitwa nad Bzurą',
      description: 'Największa polska bitwa kampanii wrześniowej.',
      requires: [{ type: 'completed', scenarioId: 'krojanty' }],
    },
    {
      scenarioId: 'warszawa',
      name: 'Obrona Warszawy',
      description: 'Obrona stolicy przed wojskami niemieckimi.',
      requires: [{ type: 'completed', scenarioId: 'bzura' }],
    },
    {
      scenarioId: 'kock',
      name: 'Bitwa pod Kockiem',
      description: 'Ostatnia wielka bitwa kampanii wrześniowej.',
      requires: [{ type: 'completed', scenarioId: 'warszawa' }],
    },
  ],
};

export const ALL_CAMPAIGNS: Campaign[] = [POLISH_SEPTEMBER_CAMPAIGN];

// ── Progress persistence ─────────────────────────────────────────────

const STORAGE_PREFIX = 'campaign-progress-';

export function loadCampaignProgress(campaignId: string): CampaignProgress {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + campaignId);
    if (raw) {
      const parsed = JSON.parse(raw) as CampaignProgress;
      if (parsed && typeof parsed.completed === 'object') return parsed;
    }
  } catch { /* ignore corrupt data */ }
  return { completed: {} };
}

export function saveCampaignProgress(campaignId: string, progress: CampaignProgress): void {
  localStorage.setItem(STORAGE_PREFIX + campaignId, JSON.stringify(progress));
}

/** Record a mission completion, preserving best rating. */
export function recordMissionComplete(campaignId: string, scenarioId: string, stars: number): void {
  const progress = loadCampaignProgress(campaignId);
  const current = progress.completed[scenarioId] ?? 0;
  if (stars > current) {
    progress.completed[scenarioId] = stars;
    saveCampaignProgress(campaignId, progress);
  }
}

// ── Mission unlock logic ─────────────────────────────────────────────

export function isMissionUnlocked(requirements: CampaignRequirement[] | undefined, progress: CampaignProgress): boolean {
  if (!requirements || requirements.length === 0) return true;
  return requirements.every(req => {
    const stars = progress.completed[req.scenarioId] ?? 0;
    switch (req.type) {
      case 'completed':
        return stars > 0;
      case 'completed-with':
        return stars >= req.minStars;
    }
  });
}
